import type { FunctionComponent } from "react";
import {
  ModalContainer,
  ModalLayoutCenter,
  Field,
  Form,
} from "@kanda-libs/ks-component-ts";
import type { UserType } from "@kanda-libs/ks-frontend-services";
import { Button, Text } from "@kanda-libs/ks-design-library";
import { REPORT_OPTIONS } from "./constants";
import { MODAL_ID } from "../constants";
import useCompanyReportModal from "./useCompanyReportModal";
import CompanyReportLineItem from "./CompanyReportLineItem";
import { copyToClipboard, formatReportKey } from "./helpers";

export interface CompanyReportModalProps {
  user: UserType;
}

const CompanyReportModal: FunctionComponent<CompanyReportModalProps> =
  function ({ user }) {
    const {
      form,
      report = {},
      submitted,
      setSubmitted,
      isSubmitting,
      onSubmit,
      copied,
      setCopied,
    } = useCompanyReportModal(user);

    return (
      <ModalContainer id={MODAL_ID} opacity={30}>
        {({ handleClose }) => (
          <ModalLayoutCenter
            id="staff-revert-job-modal"
            className="w-10/12 md:w-160"
            onClose={handleClose}
          >
            <Form id="staff-company-report" form={form} onSubmit={onSubmit}>
              {!submitted && (
                <>
                  <Field.Select name="report" options={REPORT_OPTIONS} />
                  <Button.Text
                    submit
                    id="staff-run-report"
                    label="Run report"
                    className="w-full"
                    isLoading={isSubmitting}
                  />
                  <Button.Text
                    id="staff-close-report"
                    label="Close"
                    variant="outline"
                    className="w-full mt-2"
                    isLoading={isSubmitting}
                    onClick={handleClose}
                  />
                </>
              )}
              {submitted && (
                <>
                  <Text
                    text={`Report type: ${formatReportKey(
                      form.getValues().report
                    )}`}
                    className="text-24-28-em"
                  />
                  <div className="mt-4">
                    {Object.keys(report || {}).map((reportKey) => (
                      <CompanyReportLineItem
                        reportKey={reportKey}
                        value={
                          (report[reportKey as keyof typeof report] ||
                            "") as string
                        }
                      />
                    ))}
                  </div>
                  <Button.Text
                    id="staff-copy-results"
                    label={copied ? "Copied" : "Copy results"}
                    variant="outline"
                    onClick={(e) => {
                      setCopied(true);
                      copyToClipboard(JSON.stringify(report));
                    }}
                  />
                  <Button.Text
                    submit
                    id="staff-run-report"
                    label="Re-run report"
                    variant="outline"
                    className="ml-2"
                    onClick={(e) => {
                      setSubmitted(false);
                    }}
                  />
                  <Button.Text
                    id="staff-close-report"
                    label="Close"
                    variant="outline"
                    className="ml-2"
                    isLoading={isSubmitting}
                    onClick={handleClose}
                  />
                </>
              )}
            </Form>
          </ModalLayoutCenter>
        )}
      </ModalContainer>
    );
  };

export default CompanyReportModal;
